import { Link } from 'react-router-dom';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import Seo from '../Components/Seo';
import { contactInfo } from '../data/siteNav';
import { breadcrumbSchema, SITE_NAME } from '../data/seo';
import '../assests/css/privacy-policy.css';

const updated = '4 October 2026';

const sections = [
  { id: 'who-we-are', title: '1. Who we are' },
  { id: 'scope', title: '2. What this policy covers' },
  { id: 'roles', title: '3. The school and Bright Study' },
  { id: 'parent-app', title: '4. Brightstudy Parent app' },
  { id: 'website', title: '5. Website and enquiries' },
  { id: 'use', title: '6. How we use information' },
  { id: 'sharing', title: '7. Who we share information with' },
  { id: 'children', title: '8. Children' },
  { id: 'security', title: '9. Security and retention' },
  { id: 'rights', title: '10. Your choices and rights' },
  { id: 'changes', title: '11. Changes to this policy' },
  { id: 'contact', title: '12. Contact us' },
];

function PrivacyPolicy() {
  return (
    <div className="privacy-page">
      <Seo
        path="/privacy-policy"
        schema={[breadcrumbSchema('/privacy-policy', 'Privacy Policy')]}
      />
      <Header />
      <main>
        <section className="privacy-hero">
          <div className="container">
            <p className="privacy-kicker">Bright Study / Legal</p>
            <h1>Privacy Policy</h1>
            <p className="privacy-lead">
              This policy explains how {SITE_NAME} collects, uses, and shares information in the
              Brightstudy Parent app, parent web login, and on brightstudy.in.
            </p>
            <p className="privacy-updated">Last updated: {updated}</p>
          </div>
        </section>

        <section className="privacy-body">
          <div className="container privacy-layout">
            <nav className="privacy-toc" aria-label="On this page">
              <p>On this page</p>
              <ol>
                {sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>{section.title}</a>
                  </li>
                ))}
              </ol>
            </nav>

            <article className="privacy-copy">
              <section id="who-we-are">
                <h2>1. Who we are</h2>
                <p>
                  {SITE_NAME} BrightStudy is a School ERP and educational management platform that helps schools manage academic, administrative, attendance, communication, fee management, and other school-related activities.
Our application may be used by:</p>
<ul><li>- Students</li>
<li>- Parents/Guardians</li>
<li>- Teachers</li>
<li>- School Administrators</li>
<li>- School Staff</li>
<li>- Authorized School Representatives</li>
</ul>                
                
              </section>

              <section id="scope">
                <h2>2. What this policy covers</h2>
                <p>This policy applies to:</p>
                <ul>
                  <li>The Brightstudy Parent app on Android and, where offered, iOS.</li>
                  <li>The parent and student web login that shows the same school records.</li>
                  <li>
                    The public website at brightstudy.in, including demo, contact, career, speaker,
                    newsletter, and pricing forms.
                  </li>
                </ul>
                <p>
                  School staff tools, such as the teacher or admin app, follow the agreement between
                  Bright Study and the school. This page is written for parents, guardians, and
                  visitors to the public website.
                </p>
              </section>

              <section id="roles">
                <h2>3. The school and Bright Study</h2>
                <p>
                  A school creates the parent account and decides which student records are stored.
                  For attendance, fees, homework, circulars, remarks, and transport updates, the
                  school is responsible for the student and parent information. Bright Study stores
                  and displays that information so the school can run its campus.
                </p>
                <p>
                  Bright Study is responsible for information you give us directly on this website,
                  such as a demo request or a license purchase.
                </p>
              </section>

              <section id="parent-app">
                <h2>4. Information in the Brightstudy Parent app</h2>
                <p>When your school gives you access, the app and parent login can include:</p>
                <ul>
                  <li>
                    <strong>Account details.</strong> Your name, mobile number, email if the school
                    has it, login identifier, school name, and the student or students linked to you.
                  </li>
                  <li>
                    <strong>Student and school records the school chooses to show.</strong> Class and
                    section, attendance, homework, learning materials, circulars, teacher remarks,
                    exam or progress information, fee dues, receipts, and transport updates.
                  </li>
                  <li>
                    <strong>Messages.</strong> Notices the school sends through the app, push
                    notification, SMS, or WhatsApp.
                  </li>
                  <li>
                    <strong>Fee payments.</strong> If you pay a school fee in the app, the payment
                    is handled by the payment provider the school uses. We receive a payment status,
                    amount, and reference so the school can issue a receipt. We do not store your
                    full card number, UPI PIN, or net-banking password.
                  </li>
                  <li>
                    <strong>Device information needed to run the app.</strong> Device type, operating
                    system, app version, and a push-notification token so school alerts can reach
                    the phone. We may also keep basic diagnostic information when the app fails, so
                    we can fix it.
                  </li>
                </ul>
                <p>
                  The Parent app does not ask for your phone&apos;s location. If a school shows a bus
                  route, that location comes from the school&apos;s transport setup. Face, RFID, or QR
                  attendance, where a school uses them, is recorded by the school on campus and then
                  shown in the parent account. The Parent app does not use those sensors on your phone.
                </p>
              </section>

              <section id="website">
                <h2>5. Information on the website</h2>
                <p>If you write to us from the website, we collect the fields you fill in:</p>
                <ul>
                  <li>
                    Demo requests: name, designation, mobile number, location, school name, number of
                    students, email, and the needs you describe.
                  </li>
                  <li>
                    Contact, speaker, career, and newsletter forms: name, email, mobile number, school
                    or organisation, role, topic, and the message you send, depending on the form.
                  </li>
                  <li>
                    License checkout: name, email, mobile number, the plan you select, and the
                    payment reference returned by Razorpay.
                  </li>
                </ul>
                <p>
                  Website forms are emailed to our team through FormSubmit (formsubmit.co). License
                  payments are processed by Razorpay. Razorpay collects the payment method details
                  on its own checkout page.
                </p>
                <p>
                  Our hosting logs can include the IP address, browser type, date, and the page
                  requested. We use those logs to keep the site secure and to troubleshoot errors.
                  We do not use advertising cookies, and we do not sell browsing data.
                </p>
              </section>

              <section id="use">
                <h2>6. How we use information</h2>
                <ul>
                  <li>To show parents the records their school has published.</li>
                  <li>To deliver circulars, homework alerts, fee reminders, and transport updates.</li>
                  <li>To record fee payments and receipts.</li>
                  <li>To answer a demo, sales, career, speaker, or support request.</li>
                  <li>To complete a website license purchase and provide onboarding.</li>
                  <li>To keep accounts secure, prevent misuse, and fix app or website faults.</li>
                  <li>To meet accounting, tax, and legal duties.</li>
                </ul>
                <p>
                  We use parent and student information to provide the school service. We do not sell
                  personal information, and we do not use it for third-party advertising.
                </p>
              </section>

              <section id="sharing">
                <h2>7. Who we share information with</h2>
                <p>We share information only with the parties that need it to provide the service:</p>
                <ul>
                  <li>
                    <strong>Your school.</strong> Staff authorised by the school can see the parent
                    and student records the school stores, including messages you send through the
                    platform and fee payments made to the school.
                  </li>
                  <li>
                    <strong>Razorpay.</strong> Payment processing for website license purchases, and
                    for school fee payments where the school uses Razorpay. Razorpay handles card,
                    UPI, and net-banking details under its own privacy policy.
                  </li>
                  <li>
                    <strong>SMS and WhatsApp providers.</strong> When a school sends a notice by SMS
                    or WhatsApp, the phone number and message content are shared with the messaging
                    provider the school has enabled.
                  </li>
                  <li>
                    <strong>Push-notification services.</strong> A device token is shared with the
                    notification service that delivers alerts to the app.
                  </li>
                  <li>
                    <strong>FormSubmit.</strong> Website form contents are sent through FormSubmit so
                    they reach our team by email.
                  </li>
                  <li>
                    <strong>Hosting and infrastructure providers.</strong> They store and transmit the
                    data needed to run the website, app, and school platform.
                  </li>
                  <li>
                    <strong>Authorities and advisers.</strong> We may disclose information when the
                    law requires it, or to protect the security of the platform and the people who
                    use it.
                  </li>
                </ul>
              </section>

              <section id="children">
                <h2>8. Children</h2>
                <p>
                  Brightstudy Parent is for parents and guardians. A child does not create this
                  account. The school adds the student record and links it to the parent.
                </p>
                <p>
                  Student information in the app is there so a parent can follow school life. The
                  school is responsible for collecting that information and for any consent required
                  under the Digital Personal Data Protection Act, 2023. If you believe a child&apos;s
                  information was submitted to us by mistake, write to{' '}
                  <a href={contactInfo.parentDeskHref}>{contactInfo.parentDesk}</a>.
                </p>
              </section>

              <section id="security">
                <h2>9. Security and retention</h2>
                <p>
                  School records are available only to signed-in users the school has authorised.
                  The website and app are served over HTTPS. Access inside the platform follows the
                  role the school assigns.
                </p>
                <p>
                  Parent and student records stay for as long as the school keeps the student on the
                  platform. When a school ends the service, we delete or return that data, except
                  where we must keep a copy for a legal, tax, or security reason.
                </p>
                <p>
                  Website enquiries are kept long enough to respond and to keep a business record of
                  the conversation. Payment records for license purchases are kept for as long as
                  accounting and tax rules require.
                </p>
              </section>

              <section id="rights">
                <h2>10. Your choices and rights</h2>
                <p>You can ask to access, correct, or delete personal information.</p>
                <ul>
                  <li>
                    For a student record, fee entry, attendance mark, or circular, contact your
                    school. The school decides what is stored and shown.
                  </li>
                  <li>
                    To stop push alerts, turn notifications off in the phone settings. SMS and
                    WhatsApp notices are sent by the school.
                  </li>
                  <li>
                    For a website enquiry, newsletter signup, or license purchase, write to{' '}
                    <a href={contactInfo.emailHref}>{contactInfo.email}</a>.
                  </li>
                </ul>
                <p>
                  We will respond to a request sent to the email contacts in this policy. If we need the
                  school to act, we will tell you.
                </p>
              </section>

              <section id="changes">
                <h2>11. Changes to this policy</h2>
                <p>
                  When this policy changes, we will update this page and the date at the top. The
                  current version is always available at brightstudy.in/privacy-policy, with no login
                  required.
                </p>
              </section>

              <section id="contact">
                <h2>12. Contact us</h2>
                <p>
                  {SITE_NAME}
                </p>
                <p>
                  Email: <a href={contactInfo.emailHref}>{contactInfo.email}</a>
                  <br />
                  Parent desk: <a href={contactInfo.parentDeskHref}>{contactInfo.parentDesk}</a>
                  <br />
                  Phone: <a href={contactInfo.phoneHref}>{contactInfo.phone}</a>
                </p>
                <p>
                  <Link to="/contact-us">Contact page</Link>
                </p>
              </section>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
